import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from "react";
import { SHELL_TEXT } from "../content.js";
import { IconAlert, IconCheck, IconCircleCheck, IconClose, IconInfo, IconOffline, IconWarning } from "../icons.js";
import { cx } from "../link.js";
import { Button } from "./actions.js";
const TONE_ICON = {
    info: IconInfo,
    success: IconCircleCheck,
    warning: IconWarning,
    error: IconAlert,
};
/**
 * Aviso dentro da página: sempre ícone e texto, nunca só cor (DESIGN_SYSTEM §3.1). Erro diz o que aconteceu
 * e o que fazer agora (§7). `live` anuncia ao leitor de tela quando o aviso aparece depois de uma ação.
 */
export function Notice({ tone = "info", title, children, live, action }) {
    const Icon = TONE_ICON[tone];
    const role = live ? (tone === "error" ? "alert" : "status") : undefined;
    return (_jsxs("div", { className: `we-notice we-notice--${tone}`, role: role, children: [_jsx(Icon, { className: "we-notice__icon" }), _jsxs("div", { className: "we-notice__body", children: [title && _jsx("p", { className: "we-notice__title", children: title }), children && _jsx("div", { className: "we-notice__text", children: children }), action && _jsx("div", { className: "we-notice__action", children: action })] })] }));
}
/** Etiqueta curta (ex.: "Pronta para aplicar", tempo, objetivo). */
export function Tag({ tone = "neutral", icon: Icon, children }) {
    const TagIcon = Icon ?? (tone === "success" ? IconCheck : undefined);
    return (_jsxs("span", { className: `we-tag we-tag--${tone}`, children: [TagIcon && _jsx(TagIcon, {}), _jsx("span", { children: children })] }));
}
/**
 * Diálogo de confirmação: só para ação que perde trabalho (DESIGN_SYSTEM §6). Título em pergunta, a
 * consequência em uma frase e dois botões. Usa o <dialog> do navegador: foco preso e Esc = cancelar.
 */
export function ConfirmDialog({ open, title, children, confirmLabel, cancelLabel, onConfirm, onCancel, }) {
    const ref = useRef(null);
    const titleId = useId();
    const textId = useId();
    useEffect(() => {
        const dialog = ref.current;
        if (!dialog)
            return;
        if (open && !dialog.open)
            dialog.showModal();
        if (!open && dialog.open)
            dialog.close();
    }, [open]);
    return (_jsxs("dialog", { ref: ref, className: "we-dialog", "aria-labelledby": titleId, "aria-describedby": children ? textId : undefined, onCancel: (e) => {
            e.preventDefault();
            onCancel();
        }, children: [_jsx("h2", { id: titleId, className: "we-dialog__title", children: title }), children && (_jsx("div", { id: textId, className: "we-dialog__text", children: children })), _jsxs("div", { className: "we-dialog__actions", children: [_jsx(Button, { variant: "secondary", onClick: onCancel, autoFocus: true, children: cancelLabel }), _jsx(Button, { variant: "quiet", onClick: onConfirm, children: confirmLabel })] })] }));
}
/**
 * Aviso passageiro: só confirmação ("Salvo"), nunca erro que exige ação (DESIGN_SYSTEM §6). Some sozinho
 * depois de `duration` e pode ser fechado antes.
 */
export function Toast({ open, children, onClose, duration = 6000 }) {
    useEffect(() => {
        if (!open)
            return;
        const timer = window.setTimeout(onClose, duration);
        return () => window.clearTimeout(timer);
    }, [open, duration, onClose]);
    return (_jsx("div", { className: "we-toast-region", role: "status", "aria-live": "polite", children: open && (_jsxs("div", { className: "we-toast", children: [_jsx(IconCircleCheck, { className: "we-toast__icon" }), _jsx("span", { className: "we-toast__text", children: children }), _jsx("button", { type: "button", className: "we-toast__close", "aria-label": SHELL_TEXT.fechar, onClick: onClose, children: _jsx(IconClose, {}) })] })) }));
}
/**
 * Espera com barra e etapas na língua do professor (DESIGN_SYSTEM §6; D78). O app controla o avanço
 * (por tempo estimado); este componente só mostra.
 */
export function WaitProgress({ label, value, stages, current }) {
    const pct = Math.max(0, Math.min(100, Math.round(value)));
    return (_jsxs("div", { className: "we-wait", children: [_jsx("div", { className: "we-wait__bar", role: "progressbar", "aria-label": label, "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": pct, "aria-valuetext": stages[current], children: _jsx("span", { className: "we-wait__fill", style: { inlineSize: `${pct}%` } }) }), _jsx("ol", { className: "we-wait__stages", children: stages.map((stage, i) => {
                    const state = i < current ? "feita" : i === current ? "atual" : "proxima";
                    return (_jsxs("li", { className: "we-wait__stage", "data-etapa": state, "aria-current": i === current ? "step" : undefined, children: [_jsx("span", { className: "we-wait__mark", "aria-hidden": "true", children: i < current ? _jsx(IconCheck, {}) : null }), _jsx("span", { children: stage })] }, stage));
                }) })] }));
}
/** Estado vazio: ensina o próximo passo com um exemplo; nunca "nada aqui" (DESIGN_SYSTEM §7). */
export function EmptyState({ icon: Icon, title, children, action }) {
    return (_jsxs("div", { className: "we-empty", children: [Icon && _jsx(Icon, { className: "we-empty__icon" }), _jsx("p", { className: "we-empty__title", children: title }), children && _jsx("div", { className: "we-empty__text", children: children }), action && _jsx("div", { className: "we-empty__action", children: action })] }));
}
/** Carregando: blocos com a forma do conteúdo, parados (nada em loop; DESIGN_SYSTEM §3.4 e §7). */
export function Skeleton({ lines = 3, className }) {
    return (_jsx("div", { className: cx("we-skeleton", className), "aria-hidden": "true", children: Array.from({ length: lines }, (_, i) => (_jsx("span", { className: "we-skeleton__line" }, i))) }));
}
/** Sem internet: aviso fixo e discreto; o que já carregou continua legível (DESIGN_SYSTEM §7). */
export function OfflineNotice({ text = SHELL_TEXT.semInternet }) {
    // Começa como no HTML gerado no build (sem aviso) e confere a conexão depois de montar (D113).
    const [offline, setOffline] = useState(false);
    useEffect(() => {
        setOffline(navigator.onLine === false);
        const on = () => setOffline(false);
        const off = () => setOffline(true);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => {
            window.removeEventListener("online", on);
            window.removeEventListener("offline", off);
        };
    }, []);
    return (_jsx("div", { className: "we-offline-region", role: "status", children: offline && (_jsxs("p", { className: "we-offline", children: [_jsx(IconOffline, {}), _jsx("span", { children: text })] })) }));
}
